/**
 * Guilloche / Spirograph engine — ported from halo.js to TypeScript.
 * Generates animated halos via hypotrochoid math + Canvas 2D rendering.
 */

// ── Types ──

export interface Point {
  x: number;
  y: number;
}

export interface RGBA {
  r: number;
  g: number;
  b: number;
  a: number;
}

export interface RingConfig {
  innerRadius: number;
  outerRadius: number;
  frequency: number;
  amplitude: number;
  waveform: 'sin' | 'hypo' | 'rose';
  color: RGBA;
  lineWidth: number;
  glowPasses: number;
  glowSpread: number;
  rotationSpeed: number;
}

export interface GuillocheLayerParams {
  R: number;
  r: number;
  d: number;
  resolution: number;
  phase: number;
  modFreq: number;
  modDepth: number;
}

export interface HaloConfig {
  rings: RingConfig[];
  guillocheParams: GuillocheLayerParams[];
  guillocheScale: number;
  guillocheColor: RGBA;
  guillocheLineWidth: number;
  guillocheBlendMode: GlobalCompositeOperation;
  breatheSpeed: number;
  breatheDepth: number;
  fillColor: RGBA;
  fillRadius: number;
  fillBlendMode: GlobalCompositeOperation;
  borderColor: RGBA;
  borderWidth: number;
  ringBlendMode: GlobalCompositeOperation;
  globalRotationSpeed: number;
}

// ── Math primitives ──

function hypotrochoid(angle: number, R: number, r: number, d: number, phase = 0): Point {
  const diff = R - r;
  const ratio = diff / r;
  const a = angle + phase;
  return {
    x: diff * Math.cos(a) + d * Math.cos(ratio * a),
    y: diff * Math.sin(a) - d * Math.sin(ratio * a),
  };
}

function roseCurve(angle: number, amplitude: number, frequency: number, phase = 0): Point {
  const a = angle + phase;
  const r = amplitude * Math.cos(frequency * a);
  return { x: r * Math.cos(a), y: r * Math.sin(a) };
}

function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a;
}

function lcm(a: number, b: number): number {
  return Math.abs(a * b) / gcd(a, b);
}

// ── Point generators ──

function generateGuillochePoints(params: GuillocheLayerParams): Point[] {
  const { R, r, d, resolution, phase, modFreq, modDepth } = params;
  const points: Point[] = [];
  const period = lcm(Math.round(R * 100), Math.round(r * 100)) / Math.round(R * 100);
  const totalAngle = 2 * Math.PI * Math.min(period, 50);

  for (let i = 0; i <= resolution; i++) {
    const angle = (i / resolution) * totalAngle;
    const pt = hypotrochoid(angle, R, r, d, phase);
    const mod = 1 - modDepth * (0.5 + 0.5 * Math.sin(modFreq * angle));
    points.push({ x: pt.x * mod, y: pt.y * mod });
  }
  return points;
}

function generateRingPoints(
  innerRadius: number,
  outerRadius: number,
  frequency: number,
  amplitude: number,
  resolution: number,
  phase = 0,
  waveform: 'sin' | 'hypo' | 'rose' = 'sin',
): Point[] {
  const points: Point[] = [];
  const mid = (innerRadius + outerRadius) / 2;
  const range = (outerRadius - innerRadius) / 2;
  const amp = Math.min(amplitude, 1) * range;

  for (let i = 0; i <= resolution; i++) {
    const angle = (i / resolution) * Math.PI * 2;
    let displacement: number;

    switch (waveform) {
      case 'hypo': {
        const pt = hypotrochoid(angle * frequency, 1, 0.3, 0.7, phase);
        displacement = amp * (pt.x + pt.y) * 0.5;
        break;
      }
      case 'rose': {
        const pt = roseCurve(angle, 1, frequency, phase);
        displacement = amp * Math.sqrt(pt.x * pt.x + pt.y * pt.y);
        break;
      }
      default:
        displacement = amp * Math.sin(frequency * angle + phase);
    }

    const r = mid + displacement;
    points.push({ x: r * Math.cos(angle), y: r * Math.sin(angle) });
  }
  return points;
}

// ── Renderer ──

export class GuillocheRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private config: HaloConfig;
  private time = 0;
  private animationId: number | null = null;
  private ringPointsCache = new Map<number, Point[]>();
  private guillochePointsCache: Point[][] = [];
  private centerX = 0;
  private centerY = 0;
  private baseRadius = 0;
  private dpr = 1;

  constructor(canvas: HTMLCanvasElement, config: HaloConfig) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
    this.config = structuredClone(config);
    this.resize();
    this.cachePoints();
  }

  resize() {
    this.dpr = Math.min(window.devicePixelRatio, 2);
    const rect = this.canvas.getBoundingClientRect();
    this.canvas.width = rect.width * this.dpr;
    this.canvas.height = rect.height * this.dpr;
    this.centerX = this.canvas.width / 2;
    this.centerY = this.canvas.height / 2;
    this.baseRadius = Math.min(this.canvas.width, this.canvas.height) / 2;
    this.cachePoints();
  }

  private cachePoints() {
    this.ringPointsCache.clear();
    this.config.rings.forEach((ring, i) => {
      this.ringPointsCache.set(
        i,
        generateRingPoints(
          ring.innerRadius * this.baseRadius,
          ring.outerRadius * this.baseRadius,
          ring.frequency,
          ring.amplitude,
          ring.frequency * 20,
          0,
          ring.waveform,
        ),
      );
    });
    this.guillochePointsCache = this.config.guillocheParams.map(generateGuillochePoints);
  }

  start() {
    if (this.animationId !== null) return;
    const loop = (ts: number) => {
      this.time = ts * 0.001;
      this.render();
      this.animationId = requestAnimationFrame(loop);
    };
    this.animationId = requestAnimationFrame(loop);
  }

  stop() {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  private render() {
    const { ctx, centerX, centerY, baseRadius, config, time } = this;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    const breathe = 1 + config.breatheDepth * Math.sin(time * config.breatheSpeed);
    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.scale(breathe, breathe);
    if (config.globalRotationSpeed) ctx.rotate(time * config.globalRotationSpeed);

    // Fill glow
    const { r: fr, g: fg, b: fb, a: fa } = config.fillColor;
    const fillR = baseRadius * config.fillRadius;
    ctx.save();
    ctx.globalCompositeOperation = config.fillBlendMode;
    const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, fillR);
    grad.addColorStop(0, `rgba(${fr},${fg},${fb},${Math.min(fa * 3, 1)})`);
    grad.addColorStop(0.5, `rgba(${fr},${fg},${fb},${fa * 2})`);
    grad.addColorStop(0.85, `rgba(${fr},${fg},${fb},${fa * 1.5})`);
    grad.addColorStop(1, `rgba(${fr},${fg},${fb},0)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(0, 0, fillR, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Guilloche
    ctx.save();
    ctx.globalCompositeOperation = config.guillocheBlendMode;
    this.renderGuilloche(ctx, time);
    ctx.restore();

    // Rings
    ctx.save();
    ctx.globalCompositeOperation = config.ringBlendMode;
    this.config.rings.forEach((ring, i) => this.renderRing(ctx, ring, i, time));
    ctx.restore();

    // Border circles
    if (config.borderWidth > 0) {
      const { r, g, b, a } = config.borderColor;
      ctx.strokeStyle = `rgba(${r},${g},${b},${a})`;
      ctx.lineWidth = config.borderWidth * this.dpr;
      ctx.beginPath();
      ctx.arc(0, 0, baseRadius * 0.5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = `rgba(${r},${g},${b},${a * 0.4})`;
      ctx.lineWidth = config.borderWidth * this.dpr * 0.5;
      ctx.beginPath();
      ctx.arc(0, 0, baseRadius * 0.36, 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.restore();
  }

  private renderGuilloche(ctx: CanvasRenderingContext2D, time: number) {
    const scale = this.config.guillocheScale * this.baseRadius;
    const { r, g, b, a } = this.config.guillocheColor;

    this.guillochePointsCache.forEach((points, i) => {
      if (points.length < 2) return;
      const rot = time * 0.05 * (i % 2 === 0 ? 1 : -1);
      const alpha = a * (1 - i * 0.03);
      ctx.save();
      ctx.rotate(rot);
      ctx.strokeStyle = `rgba(${r},${g},${b},${alpha})`;
      ctx.lineWidth = this.config.guillocheLineWidth * this.dpr;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(points[0].x * scale, points[0].y * scale);
      for (let j = 1; j < points.length; j++) {
        ctx.lineTo(points[j].x * scale, points[j].y * scale);
      }
      ctx.stroke();
      ctx.restore();
    });
  }

  private renderRing(ctx: CanvasRenderingContext2D, ring: RingConfig, _i: number, time: number) {
    const phase = time * ring.rotationSpeed;
    const points = generateRingPoints(
      ring.innerRadius * this.baseRadius,
      ring.outerRadius * this.baseRadius,
      ring.frequency,
      ring.amplitude,
      ring.frequency * 20,
      phase,
      ring.waveform,
    );
    if (points.length < 2) return;

    const { r, g, b, a } = ring.color;
    for (let pass = ring.glowPasses; pass >= 0; pass--) {
      const w = 1 + pass * ring.glowSpread;
      const alpha = a / (pass + 1);
      ctx.strokeStyle = `rgba(${r},${g},${b},${alpha})`;
      ctx.lineWidth = ring.lineWidth * this.dpr * w;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let j = 1; j < points.length; j++) ctx.lineTo(points[j].x, points[j].y);
      ctx.closePath();
      ctx.stroke();
    }
  }

  updateConfig(partial: Partial<HaloConfig>) {
    Object.assign(this.config, partial);
    this.cachePoints();
  }

  getConfig(): HaloConfig {
    return structuredClone(this.config);
  }

  dispose() {
    this.stop();
    this.ringPointsCache.clear();
    this.guillochePointsCache = [];
  }
}
